import axios from "axios";
import { authenticator } from "../Authentication/auth-funcs";
import { getAuthHeaders } from "./authHeaders";
import logger from "../utils/logger";
import { getAttribution } from "../utils/attribution";
import { userTimeZone } from "../utils/userTimeZone";
import { isRateLimited, rateLimitMessage } from "./apiErrors";

class BackendConnector {
  static instance;
  baseUrl = process.env.REACT_APP_API_DOMAIN;

  static getInstance() {
    if (!BackendConnector.instance) {
      BackendConnector.instance = new BackendConnector();
    }
    return BackendConnector.instance;
  }

  async makeRequest(method, endpoint, data, config = {}) {
    try {
      const headers = await getAuthHeaders();
      const url = `${this.baseUrl}${endpoint}`;

      const requestConfig = {
        method,
        url,
        headers,
        data,
        withCredentials: true,
        ...config,
      };

      const response = await axios(requestConfig);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const err = new Error(
          error.response?.status === 401
            ? "Session expired or unauthorized"
            : isRateLimited(error)
              ? rateLimitMessage(error.response?.data)
              : error.response?.data?.message || error.message
        );
        // Structured info for callers that need to branch on the specific
        // failure (e.g. a 503 PROVIDER_NOT_CONFIGURED) instead of just the
        // message string.
        err.status = error.response?.status;
        err.code = error.response?.data?.code;
        // The next-stage routes name their refusal instead of only failing: the draft and
        // prep-pack routes put it at the top level as `reason`; the send route sends it both
        // as `reason` and under `data.refusal` (daily_cap, send_in_progress,
        // send_outcome_unknown, send_failed, …). Both are carried
        // through here so a caller can tell "we refused, and here is why" from "something
        // broke" — the difference between an explanation and an error dialog.
        err.reason = error.response?.data?.reason;
        err.refusal = error.response?.data?.data?.refusal;
        // Billing routes answer 409 for a plan the App Store bills; the web
        // app cannot change or cancel it and has to send the user to Apple.
        err.billedBy = error.response?.data?.billedBy;
        err.manageUrl = error.response?.data?.manageUrl;
        // /createCheckoutSession answers 409 with this flag when the account
        // already has a subscription: the change has to go through /updateSubscription.
        err.useUpdateSubscription = error.response?.data?.useUpdateSubscription === true;
        // A renewal that has not been paid (402/409 from checkout and plan
        // changes): the user has to update the card before anything else.
        err.paymentFailed = error.response?.data?.paymentFailed === true;
        // /createCheckoutSession 409: the account already owns this lifetime plan.
        err.alreadyOwned = error.response?.data?.alreadyOwned === true;
        throw err;
      }
      throw error;
    }
  }

  async makeUnauthenticatedRequest(method, endpoint, data, config = {}) {
    try {
      const url = `${this.baseUrl}${endpoint}`;

      logger.log(`[makeUnauthenticatedRequest] ${method} to ${url}`);

      const requestConfig = {
        method,
        url,
        data,
        withCredentials: false,
        ...config
      };

      const response = await axios(requestConfig);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        logger.error('[makeUnauthenticatedRequest] Axios Error:', {
          message: error.message,
          code: error.code,
          config: error.config,
          response: error.response ? {
            status: error.response.status,
            data: error.response.data
          } : 'No response'
        });
        const err = new Error(
          isRateLimited(error)
            ? rateLimitMessage(error.response?.data)
            : error.response?.data?.message || error.message
        );
        err.status = error.response?.status;
        throw err;
      }
      throw error;
    }
  }

  // Shared wrapper for the common per-method pattern: make the request and,
  // on failure, log + rethrow a normalized Error. The options preserve each
  // method's original logging/return behavior exactly:
  //   unwrap          — return response.data instead of response
  //   logErrorObject  — some call sites logged only the message, without error
  //   unauthenticated — use makeUnauthenticatedRequest instead of makeRequest
  async requestOrThrow({
    method,
    endpoint,
    data,
    config,
    unauthenticated = false,
    unwrap = false,
    logMessage,
    logErrorObject = true,
    failMessage,
  }) {
    try {
      const response = unauthenticated
        ? await this.makeUnauthenticatedRequest(method, endpoint, data, config)
        : await this.makeRequest(method, endpoint, data, config);
      return unwrap ? response.data : response;
    } catch (error) {
      if (logErrorObject) {
        logger.error(logMessage, error);
      } else {
        logger.error(logMessage);
      }
      const normalized = new Error(error.message || failMessage);
      if (error.status !== undefined) {
        normalized.status = error.status;
      }
      if (error.code !== undefined) {
        normalized.code = error.code;
      }
      if (error.billedBy !== undefined) {
        normalized.billedBy = error.billedBy;
        normalized.manageUrl = error.manageUrl;
      }
      if (error.useUpdateSubscription) {
        normalized.useUpdateSubscription = true;
      }
      if (error.paymentFailed) {
        normalized.paymentFailed = true;
      }
      if (error.alreadyOwned) {
        normalized.alreadyOwned = true;
      }
      throw normalized;
    }
  }

  // Kicks off a forwarding test: the backend emails a tokenized message to the
  // user's tagged alias (derived from option + base address) and marks the
  // profile's emailProxyStatus "forwarding_confirmed" when the forwarded copy
  // arrives at the ingest address.
  //
  // The 200 body includes `code` (8-char A-Z0-9 string) and `startedAt`
  // (epoch seconds) alongside message/to. Test state is client-held (the
  // shared lib's profile whitelist strips server-side test state), so the
  // caller MUST capture both and echo them verbatim on every status poll.
  async startEmailProxyTest({ emailProxyOption, emailProxyBaseAddress }) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) {
      throw new Error("No user ID found");
    }
    return this.makeRequest(
      "POST",
      `/users/${user.userId}/email-proxy/test/start`,
      { emailProxyOption, emailProxyBaseAddress }
    );
  }

  // Returns { status: "not_started" | "pending" | "confirmed" } for the
  // forwarding test identified by `code`/`started` (both from the start
  // response, echoed verbatim). If they're omitted, invalid, or older than
  // 15 minutes, the backend answers from persisted profile status only and
  // can then never return "pending" — so callers must always pass them.
  async getEmailProxyTestStatus(params) {
    // Not a destructured default — that only guards undefined; null must not throw.
    const { code, started } = params || {};
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) {
      throw new Error("No user ID found");
    }
    return this.makeRequest(
      "GET",
      `/users/${user.userId}/email-proxy/test/status`,
      null,
      { params: { code, started } }
    );
  }

  // Starts the OAuth connect flow for an email-proxy provider. The backend
  // returns { redirectUrl } — the provider consent URL the caller should
  // navigate to. Auth rides in the standard headers, never in the URL.
  async startEmailProxyConnect(userId, { provider, mode, returnTo }) {
    if (!userId) {
      throw new Error("No user ID found");
    }
    return this.makeRequest(
      "GET",
      `/users/${userId}/email-proxy/connect/start`,
      null,
      { params: { provider, mode, returnTo } }
    );
  }

  /** Ends our access to a connected mailbox. Omit accountAddress to disconnect all of them. */
  async disconnectEmailProxy(userId, accountAddress) {
    if (!userId) {
      throw new Error("No user ID found");
    }
    return this.makeRequest(
      "POST",
      `/users/${userId}/email-proxy/disconnect`,
      accountAddress ? { accountAddress } : {}
    );
  }

  // Managed Gmail — the account JobHarvest creates for the user (plan D5/D7). Every route is
  // scoped to the caller's own userId server-side. Non-2xx surfaces as an Error carrying
  // `status` and the backend's `code` (FEATURE_DISABLED, MISSING_DETAILS, ALREADY_MANAGED).
  async getMailAccount(userId) {
    return this.makeRequest("GET", `/users/${userId}/mail-account`, null);
  }

  async createMailAccount(userId) {
    return this.makeRequest("POST", `/users/${userId}/mail-account`, {});
  }

  async getMailSession(userId, sessionId) {
    return this.makeRequest("GET", `/users/${userId}/mail-session/${sessionId}`, null);
  }

  /** Echo `answerSeq` from the last session read; `{ accepted: false }` means re-read and re-ask. */
  async respondMailSession(userId, sessionId, { answer, answerSeq }) {
    return this.makeRequest("POST", `/users/${userId}/mail-session/${sessionId}`, { answer, answerSeq });
  }

  async cancelMailSession(userId, sessionId) {
    return this.makeRequest("DELETE", `/users/${userId}/mail-session/${sessionId}`, null);
  }

  /** Never cached client-side: fetched on the reveal click and dropped again after it. */
  async getMailAccountCredentials(userId) {
    return this.makeRequest("GET", `/users/${userId}/mail-account/credentials`, null);
  }

  /** Removes jobs from My Jobs by URL, from any stage but Applying. */
  async deleteJobs(jobUrls) {
    return this.makeRequest("POST", "/deleteJobs", { jobUrls });
  }

  async updateUserProfile(newData) {
    try {
      const user = await authenticator.getCurrentUser();
      if (!user?.userId) {
        throw new Error("No user ID found");
      }

      // The body says what was not stored: `rejected: [{ field, code, message }]` (absent from servers
      // that predate it). See pages/Dashboard/Profile/profileSave.js.
      return await this.makeRequest("POST", `/users/${user.userId}/profile`, newData);
    } catch (error) {
      logger.error("Error updating user profile:", error);
      throw new Error(
        `Failed to update profile: ${error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  // Profile-tab "Common questions": catalog + current answers, and the story prompts.
  async getCommonQuestions() {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("GET", `/users/${user.userId}/commonQuestions`);
    return response.data;
  }

  // answers: [{ classification, value }] — value "" clears. Returns the refreshed catalog.
  async saveCommonQuestions(answers) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("POST", `/users/${user.userId}/commonQuestions`, { answers });
    return response.data;
  }

  // Answer review loop: what we wrote for the user's applications and their verdicts.
  async getPendingAnswers() {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("GET", `/users/${user.userId}/answers/pending`);
    return response.data; // { pending, batch, applications }
  }

  async getApplicationAnswers(jobKey) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("GET", `/users/${user.userId}/applications/${encodeURIComponent(jobKey)}/answers`);
    return response.data;
  }

  /* Next stage (NEXT-STAGE-PLAN essential 5). Three routes, all `sub`-scoped by the path
     userId, all keyed by the same jobKey the answer archive uses.

     Each returns `{ message, data }` and each `data` is unwrapped here, so a 200 whose body
     carries no `data` is a broken response rather than an empty one — the callers treat a
     missing payload as an error instead of rendering a calm blank screen over it. */

  /**
   * The résumé grade (NEXT_STAGE_INTAKE), five deterministic checks with a fix each.
   * A 404 is the flag being off, not an error, so it comes back as null and the caller shows nothing.
   */
  async getResumeGrade() {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    try {
      const response = await this.makeRequest("GET", `/users/${user.userId}/resume-grade`, null);
      return response?.data?.grade ?? null;
    } catch (error) {
      // makeRequest rethrows as a plain Error carrying `.status`; there is no `.response` on it.
      if (error?.status === 404) return null;
      throw error;
    }
  }

  /**
   * The staleness findings (NEXT_STAGE_INTAKE). The profile read already carries these; this is for
   * refreshing them on their own after the user has fixed something. An empty array is a real answer
   * — nothing is stale — and only a 404 (the flag) comes back as null.
   */
  async getStaleness() {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    try {
      const response = await this.makeRequest("GET", `/users/${user.userId}/staleness`, null);
      return response?.data?.findings ?? null;
    } catch (error) {
      if (error?.status === 404) return null;
      throw error;
    }
  }

  // GET /users/{userId}/reply?emailId=…
  // → { emailId, subject, body, logistics, usedAcknowledgement, to, alias, drafted, repliedAt? }
  // Keyed by the message, not the application: a recruiter who writes about a job we never
  // matched to an application can still be answered. `drafted` is always present; `false`
  // means the day's draft allowance is spent, the body is empty and the user writes it.
  // `repliedAt` (ISO) is set once a reply to this message has gone out. `to` is who the reply
  // will go to — the message's Reply-To when the sender's domain vouches for it. The route runs
  // the same checks a send does before drafting, so a refusal throws with err.reason set to any
  // of them: "flag_off" (404), "not_alias_owner" (403), "invalid_recipient" (400),
  // "unverified_sender" (403), "no_reply_address" (422) or "unaligned_sending_domain" (503). A
  // 400 or 404 with no reason is a bad request or a missing profile, and reads as a failure.
  //
  // The request always asks for the composer chips (`choices=1`) and names the user's zone (`tz`, an
  // IANA name) so the server can write the slot picker in the user's own clock. A backend that does not
  // know the parameters ignores them and answers exactly as before; one that does adds `choices`,
  // `slots`, `choicesKey` and `note?` to `data`. This method hands them through as the server wrote
  // them: deciding whether they are well formed is the composer's job (replyChoices.js), so a bad
  // `choices` can never cost the user the draft.
  async getReplyDraft(emailId) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const tz = userTimeZone();
    const response = await this.makeRequest("GET", `/users/${user.userId}/reply`, null, {
      params: { emailId, choices: 1, ...(tz ? { tz } : {}) },
    });
    if (!response?.data) throw new Error("The draft came back empty.");
    return response.data;
  }

  // POST /users/{userId}/reply/draft  { emailId, selections: [{ id, option, slots?, text? }], note?, tz? }
  // → the same `data` as getReplyDraft, plus `redrafted: boolean`.
  // The chips' redraft. It carries the user's choices and nothing else: ids, option ids, slot ids, the
  // short text they typed for an option, and the note. Never the text in the editor, never a label or a
  // value the server wrote: the server rebuilds those from its own records, and it must never see (or
  // be able to be told) what the user edited. Each selection is rebuilt here from its four allowed
  // fields, so a caller cannot send more by accident.
  // `redrafted: false` means the daily redraft allowance is spent or nothing changed: the caller keeps
  // the text it has. A missing or non-boolean `redrafted` reads as false for the same reason. The
  // contract does not say whether the flag rides inside `data` or beside it, so both are read.
  // `signal` lets the caller abandon a redraft a newer choice has replaced.
  async postReplyDraft(emailId, selections, { note, signal } = {}) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const tz = userTimeZone();
    const response = await this.makeRequest(
      "POST",
      `/users/${user.userId}/reply/draft`,
      {
        emailId,
        selections: (selections || []).map(({ id, option, slots, text }) => ({
          id,
          option,
          ...(slots ? { slots } : {}),
          ...(text !== undefined ? { text } : {}),
        })),
        ...(note ? { note } : {}),
        ...(tz ? { tz } : {}),
      },
      signal ? { signal } : {}
    );
    if (!response?.data) throw new Error("The redraft came back empty.");
    const redrafted = typeof response.data.redrafted === "boolean" ? response.data.redrafted : response.redrafted === true;
    return { ...response.data, redrafted };
  }

  // POST /users/{userId}/reply/send  { emailId, body, clientSendId? }
  // → { sent: true, messageId, sendNumber?, replayed?, repliedAt?, dailyCap? }
  // The alias, the recipient and the subject are read off the stored recruiter message by
  // the server; the edited body is the only content this client is allowed to supply, so who
  // a reply goes to is never this client's choice.
  // `clientSendId` is the idempotency key (REPLY-SPEC A1): the same id sent twice is sent
  // once, and the second answer is the first send's result with `replayed: true`.
  // A refusal throws with err.refusal set (see REPLY_REFUSAL_COPY).
  async sendReply(emailId, body, clientSendId) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest(
      "POST",
      `/users/${user.userId}/reply/send`,
      clientSendId ? { emailId, body, clientSendId } : { emailId, body }
    );
    if (!response?.data) throw new Error("The send came back empty.");
    return response.data;
  }

  // GET /users/{userId}/scoreboard → the My Jobs header's counts, the JobHarvest average and up
  // to three suggestions. Always 200: with the flag off the server sends the same shape with every
  // number withheld, so there is no "unavailable" branch to write here.
  async getScoreboard() {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("GET", `/users/${user.userId}/scoreboard`);
    if (!response?.data) throw new Error("The scoreboard came back empty.");
    return response.data;
  }

  // GET /users/{userId}/applications/{jobKey}/prep-pack
  // → { prepPack: PrepPack | null, reminder?, prepPackFor: null | { emailId, messageType,
  //     receivedAt }, message: null | { emailId, messageType, receivedAt, outcome,
  //     senderDomain?, reply: { available, refusal? } } }       (REPLY-SPEC A3, D3, D4, E1)
  // `receivedAt` is ISO. `outcome` is pack | preparing | lower_stage | unverified_sender |
  // daily_cap | no_pack. A 404 carries err.reason "flag_off" (the feature is off) or
  // "no_application" (no archive for this jobKey). My Jobs asks once per dialog open and shows
  // the tab only when there is a pack or a message (REPLY-SPEC B1); the tab's "Check again"
  // asks once more while a pack is being prepared.
  async getPrepPack(jobKey) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest(
      "GET",
      `/users/${user.userId}/applications/${encodeURIComponent(jobKey)}/prep-pack`
    );
    if (!response?.data) throw new Error("The prep pack came back empty.");
    return response.data;
  }

  // GET /users/{userId}/applications/{jobKey}/emails → { thread: ThreadMessage[] }, oldest first,
  // single-message chains included. Opening it marks the chain's received mail read on the server.
  async getApplicationEmails(jobKey) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest(
      "GET",
      `/users/${user.userId}/applications/${encodeURIComponent(jobKey)}/emails`
    );
    if (!response?.data) throw new Error("The emails came back empty.");
    return response.data;
  }

  // POST /users/{userId}/up-next  { clear: [{ jobUrl, stage, emailId? }], restore: [jobUrl] }
  // → { upNextCleared }: clears items from My Jobs' Next steps (or brings them back, the undo).
  async updateUpNext({ clear = [], restore = [] }) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("POST", `/users/${user.userId}/up-next`, { clear, restore });
    if (!response?.data) throw new Error("Next steps did not save.");
    return response.data;
  }

  // review: { status: 'liked'|'edited'|'rejected', editedValue?, reason? }
  async reviewAnswer(jobKey, answerId, review) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("POST", `/users/${user.userId}/applications/${encodeURIComponent(jobKey)}/answers/review`, { answerId, ...review });
    return response.data; // { answerId, review, pendingCount }
  }

  // Profile › Personalization: the server-compiled applicant memory.
  async getApplicantMemory() {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("GET", `/users/${user.userId}/memory`);
    return response.data; // { entries, meta }
  }

  async forgetMemoryEntry(entryId) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("POST", `/users/${user.userId}/memory`, { action: "forget", entryId });
    return response.data;
  }

  async rebuildApplicantMemory() {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const response = await this.makeRequest("POST", `/users/${user.userId}/memory`, { action: "rebuild" });
    return response.data;
  }

  // Phase 3 (NEXT-STAGE-PLAN essential 3): the résumé tailored for one job, rendered on
  // demand for the preview. `download: true` asks for the standalone PDF — the one paid
  // gate in this flow (plan section 9): the server answers 402 { code: 'PREMIUM_REQUIRED' }
  // on the free tier, which makeRequest surfaces as an Error carrying `status` and `code`.
  // The submission itself is never gated.
  async getResumePreview(jobUrl, { download = false } = {}) {
    const user = await authenticator.getCurrentUser();
    if (!user?.userId) throw new Error("No user ID found");
    const body = await this.makeRequest("POST", `/users/${user.userId}/resume-preview`, { jobUrl, download });
    // The sibling per-user handlers wrap their payload in { message, data }; the preview
    // handler's contract writes the payload bare. Accept both shapes so a wrapper on either
    // side never reads as a client bug.
    // { tailored: true, url, filename, versionId, pages, summary, skills, expiresInSeconds }
    // | { tailored: false, reason }
    const payload = body && typeof body.tailored === "boolean" ? body : body?.data;
    // Anything else is a transport or contract failure — an error page, a proxy body, a
    // renamed field. Returning it let the dialog render the not-tailored state and tell the
    // user we sent their uploaded résumé, which we cannot know from here.
    if (!payload || typeof payload.tailored !== "boolean") {
      throw new Error("Malformed resume-preview response");
    }
    return payload;
  }

  async getResumeUrl() {
    try {
      const response = await this.makeRequest("GET", "/getUserResumeLink");
      logger.log(response);
      return response.data.url;
    } catch (error) {
      logger.error("Error getting resume URL:", error);
      throw new Error(
        `Failed to get resume URL: ${error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }
  async getTranscriptUrl() {
    try {
      const response = await this.makeRequest("GET", "/getUserTranscriptLink");
      logger.log(response);
      return response.data.url;
    } catch (error) {
      logger.error("Error getting Transcript URL:", error);
      throw new Error(
        `Failed to get Transcript URL: ${error instanceof Error ? error.message : "Unknown error"
        }`
      );
    }
  }

  // In your BackendConnector class, add this method:

  // This is part of backendConnector.js (or similar)
  // No changes requested or made here, but shown for completeness of the data flow.

  async getReferralData() {
    try {
      const response = await this.makeRequest("GET", "/getReferral");
      return response.data;
      // e.g., { userId: '...', referralData: { totalReferrals: X, referrals: [] } }
    } catch (error) {
      logger.error("Error fetching referral data:", error);
      // Propagate a more informative error if possible
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch referral data";
      throw new Error(message);
    }
  }

  async addReferral() {
    try {
      const sourcePage = localStorage.getItem("referralSource");
      const referrerId = localStorage.getItem("referrerId");
      const attribution = getAttribution();

      // Post when there's either a referral invite or marketing attribution,
      // so channel data is recorded even without a referrer.
      if (sourcePage || referrerId || attribution.utmSource) {
        const response = await this.makeRequest("POST", "/addReferral", {
          referrerId,
          sourcePage: sourcePage || attribution.referrer || "direct",
          utmSource: attribution.utmSource,
          utmMedium: attribution.utmMedium,
          utmCampaign: attribution.utmCampaign,
          utmContent: attribution.utmContent,
          landingPath: attribution.landingPath,
          referrer: attribution.referrer,
          firstSeenAt: attribution.firstSeenAt,
        });
        return response.data;
      }
      return; // Explicitly return undefined if condition not met
    } catch (error) {
      logger.error("Error adding referral:", error); // Changed logger message
      const message =
        error.response?.data?.message ||
        error.message ||
        "Failed to add referral"; // Changed error message
      throw Object.assign(new Error(message), { status: error.status ?? error.response?.status });
    }
  }

  // Creator codes. Callers branch on `error.reason` (invalid_code, unknown_code,
  // own_code, already_claimed, window_closed, ...), and on `error.code` for
  // already_claimed (the code already on the account). Neither is wrapped in
  // requestOrThrow: it would drop `reason`.
  //
  // Public: a signed-out visitor has no session, and makeRequest would throw
  // before sending, so this goes out without auth headers.
  async getCreatorCode(code) {
    try {
      const response = await axios({
        method: "GET",
        url: `${this.baseUrl}/getCreatorCode`,
        params: { code },
      });
      return response.data;
    } catch (error) {
      throw this.creatorCodeError(error);
    }
  }

  async claimCreatorCode({ code, via }) {
    try {
      return await this.makeRequest("POST", "/claimCreatorCode", {
        code,
        via,
        platform: "web",
      });
    } catch (error) {
      throw this.creatorCodeError(error);
    }
  }

  // makeRequest already copies status/code/reason; a raw axios error needs the
  // same mapping.
  creatorCodeError(error) {
    if (!axios.isAxiosError(error)) return error;
    const err = new Error(error.response?.data?.message || error.message);
    err.status = error.response?.status;
    err.reason = error.response?.data?.reason;
    err.code = error.response?.data?.code;
    return err;
  }

  async getSessionStatus(sessionId) {
    try {
      const response = await this.makeRequest("GET", "/sessionStatus", null, {
        params: { session_id: sessionId },
      });
      return response;
    } catch (error) {
      logger.error("Error fetching session status in backendConnector:", error);
      throw error;
    }
  }

  async searchCompanyNames(searchTerm = "", page = 1) {
    return this.requestOrThrow({
      method: "GET",
      endpoint: "/getAllCompanyNames",
      data: null,
      config: {
        params: {
          search: searchTerm,
          page,
          limit: 50, // Number of results per page
        },
      },
      logMessage: "Error fetching company names:",
      failMessage: "Failed to fetch company names",
    });
  }

  async sendEmail({ firstName, lastName, companyName, email, inquiry }) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/sendMessage",
      data: { firstName, lastName, companyName, email, inquiry },
      unwrap: true,
      logMessage: "Error sending email:",
      failMessage: "Failed to send email",
    });
  }

  async removeExtraTranscripts(fileName) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/removeExtraTranscripts",
      data: { fileName: fileName },
      unwrap: true,
      logMessage: "Error removing extra transcripts:",
      failMessage: "Failed to remove extra transcripts",
    });
  }

  /**
   * Parses one uploaded resume. With `fileName` the server reads exactly that file and, only once
   * it parsed, makes it the profile's resume (the reply then carries `promotedFileName`). A server
   * without that support ignores `fileName` and parses the newest upload. A 422 (`err.status`)
   * means the file itself is unreadable.
   */
  async parseResume(fileName) {
    const user = await authenticator.getCurrentUser();
    return this.makeRequest("POST", "/parseResume", { userId: user?.userId, fileName });
  }

  async removeExtraResumes(fileName) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/removeExtraResumes",
      data: { fileName: fileName },
      unwrap: true,
      logMessage: "Error removing extra resumes:",
      failMessage: "Failed to remove extra resumes",
    });
  }

  async cancelSubscription() {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/cancelSubscription",
      logMessage: "Error cancelling subscription:",
      failMessage: "Failed to cancel subscription",
    });
  }
  // Same lambda the iOS app calls: revokes the stored Apple refresh token, then
  // deletes the Cognito user. Returns 502 if the revoke fails so the caller can
  // retry rather than stranding a live Apple credential on a deleted account.
  async deleteAccount() {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/deleteAccount",
      logMessage: "Error deleting account:",
      failMessage: "Failed to delete account",
    });
  }
  async createCheckoutSession(planChosen) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/createCheckoutSession",
      data: { planChosen },
      logMessage: "Error creating checkout session:",
      failMessage: "Failed to create checkout session",
    });
  }
  // Plan change for an EXISTING subscriber. There is deliberately no fallback to
  // /createCheckoutSession: that endpoint opens a *brand new* subscription, so
  // falling back on any /updateSubscription failure left the user paying for the
  // old subscription AND the new one. /updateSubscription owns the swap; a
  // failure here has to surface as a failure.
  async changeSubscription(newPlan) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/updateSubscription",
      data: { newPlan },
      logMessage: "Error changing subscription:",
      failMessage: "Failed to change subscription",
    });
  }

  // A Stripe-hosted page where the user replaces the card on file (FlashApply
  // #350). Resolves to { url }; Stripe sends the user back to
  // `${returnPath}?card_updated=1`, and the caller then retries the overdue
  // payment with /updateSubscription on the current plan.
  async createBillingPortalSession(returnPath = "/dashboard/settings") {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/billingPortalSession",
      data: { returnPath },
      logMessage: "Error opening the billing portal:",
      failMessage: "Failed to open the billing portal",
    });
  }

  // Public: the pricing page renders for signed-out visitors too. Prices come
  // from Stripe through this route, so they are typed in nowhere on the web.
  async getPlans() {
    return this.requestOrThrow({
      method: "GET",
      endpoint: "/plans",
      unauthenticated: true,
      logMessage: "Error loading plans:",
      failMessage: "Failed to load plans",
    });
  }

  // What a plan change would charge now (or that it waits for the next
  // renewal), computed by Stripe for this subscriber, shown before confirming.
  async previewSubscriptionChange(newPlan) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/previewSubscriptionChange",
      data: { newPlan },
      logMessage: "Error previewing plan change:",
      failMessage: "Failed to preview plan change",
    });
  }

  async sendWelcomeEmail(email, firstName, lastName) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/handleNewUser",
      data: { email: email, firstName: firstName, lastName: lastName },
      logMessage: "Error sending welcome email:",
      failMessage: "Failed to send welcome email",
    });
  }

  async joinWaitlist(email, platform = "ios") {
    // Public endpoint — no auth required. Adds an email to the mobile app launch waitlist.
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/joinWaitlist",
      data: { email, platform, source: "web-mobile-page" },
      unauthenticated: true,
      logMessage: "Error joining waitlist:",
      failMessage: "Failed to join waitlist",
    });
  }

  async getFiftyBasicEmailData(timestamp, view = "job") {
    // Takes in null for the first page of 50 otherwise it will take in the timestamp of the last retrieved timestamp from the previous call if the user's switching pages
    // view is "job" or "promotions" — the two lists page independently.
    // `chains: true` has the server show each conversation once across all pages, as its newest
    // message, with `threadUnreadIds` / `threadFlaggedIds` on the row. An older server ignores it.
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/getBasicEmailData",
      data: { timestamp: timestamp, view, chains: true },
      logMessage: "Error getting the 50 basic email data",
      logErrorObject: false,
      failMessage: "Failed to get 50 basic email data",
    });
  }

  async getSpecificEmail(emailId) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/getSpecificEmail",
      // `thread: true` asks for the conversation too (and has the server read the chain); without it
      // a new server answers the one message only.
      data: { emailId: emailId, thread: true },
      logMessage:
        "Error getting the specific email data that the user clicked on",
      logErrorObject: false,
      failMessage:
        "Failed to get 50 basic email data that the user clicked on",
    });
  }

  // `value` is the wanted state: a new server SETS the field to it (idempotent), an old one ignores
  // it and flips. Always pass it, so a racing second tap cannot flip a row back.
  async toggleEmailField(emailId, fieldName, value) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/toggleEmailField",
      data: { emailId: emailId, fieldName: fieldName, ...(typeof value === "boolean" ? { value } : {}) },
      logMessage: "Error deleting email notification",
      logErrorObject: false,
      failMessage: "Error deleting email notification",
    });
  }

  async updateEmailNotification(emailId, fieldName) {
    return this.requestOrThrow({
      method: "POST",
      endpoint: "/updateEmailNotification",
      data: { emailId: emailId, fieldName: fieldName },
      logMessage: "Error deleting email notification",
      logErrorObject: false,
      failMessage: "Error deleting email notification",
    });
  }
  async getAllKeptNotifications() {
    return this.requestOrThrow({
      method: "GET",
      endpoint: "/getAllKeptNotifications",
      logMessage: "Error getting the non-deleted notifications",
      logErrorObject: false,
      failMessage: "Error getting the non-deleted notifications",
    });
  }

  async getMetrics(params = {}, passcode = "") {
    try {
      // Build query string manually to avoid Axios interference.
      // SECURITY: the passcode is sent as a header, NOT a query param — query
      // strings are recorded in CloudFront/API Gateway access logs, browser
      // history, and Referer headers (CWE-598).
      const queryParams = new URLSearchParams({
        limit: '2000',
        ...params
      });

      // Force 127.0.0.1 to avoid IPv6 (::1) issues on macOS with local servers
      const baseUrl = this.baseUrl.replace('localhost', '127.0.0.1');
      const url = `${baseUrl}/metrics?${queryParams.toString()}`;

      logger.log(`[getMetrics] Fetching: ${url}`);

      const response = await fetch(url, {
        method: 'GET',
        mode: 'cors',
        headers: {
          'Accept': 'application/json',
          'x-metrics-passcode': passcode
        }
      });

      if (!response.ok) {
        if (response.status === 401) throw new Error("Unauthorized: Invalid passcode");
        throw new Error(`HTTP Error: ${response.status}`);
      }

      const json = await response.json();
      logger.log('[getMetrics] Data received:', json);
      
      // Return the 'data' field as per new documentation
      return json.data;
    } catch (error) {
      logger.error('[getMetrics] Error:', error);
      if (error instanceof Error && (error.message.includes('Failed to fetch') || error.message.includes('Network Error'))) {
        logger.warn('CORS/Network Hint: Ensure your Lambda returns "Access-Control-Allow-Origin": "*" and is running on the correct port.');
      }
      throw error;
    }
  }
}

// Export the singleton instance
export const backendConnector = BackendConnector.getInstance();
